import { MobXProviderContext } from "mobx-react";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { getErrorTitle } from "../../../../constants/constants";
import { AdmissionChecklistSection } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import { appAccentPanelStyle, appDialogContentStyle, appDialogHeaderStyle, appDialogStyle, appFormSectionStyle, appMutedTextStyle, renderDialogFooter, renderSurfaceHeader } from "../../../../components/general/uiSurfaceStyles";

interface Props {
  data: AdmissionChecklistSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const splitLines = (value: string) => value.split("\n").map((item) => normalizePlainText(item, { maxLength: 220 })).filter(Boolean);

const EditModal: FC<Props> = ({ data, isOpen, toggleModal, flag, setFlag }) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const categoryFields = (data?.checklistCategories || []).map((category, index) => ({
    key: `category_${index}`,
    title: category.title,
    items: (category.items || []).join("\n"),
  }));

  const defaultValues = categoryFields.reduce((acc, category) => {
    acc[category.key] = category.items;
    return acc;
  }, {
    enabled: data?.enabled || false,
    title: data?.title || "",
    description: data?.description || "",
  } as Record<string, any>);

  const { control, handleSubmit, reset } = useForm({ defaultValues });

  useEffect(() => {
    const nextValues = categoryFields.reduce((acc, category) => {
      acc[category.key] = category.items;
      return acc;
    }, {
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
    } as Record<string, any>);
    reset(nextValues);
  }, [data, isOpen, reset]);

  const onSubmit = async (formData: any) => {
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateTemplateSection({
        ...data,
        enabled: !!formData.enabled,
        title: normalizePlainText(formData.title, { maxLength: 140 }),
        description: normalizePlainText(formData.description, { multiline: true, maxLength: 1200 }),
        checklistCategories: (data?.checklistCategories || []).map((category, index) => ({
          title: category.title,
          items: splitLines(formData[`category_${index}`] || ""),
        })),
        modifiedAt: new Date().toISOString(),
      });
      toggleModal(false);
      setFlag(!flag);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: "Unable to save the admission checklist page.", life: 3000 });
    }
  };

  return (
    <Dialog visible={isOpen} modal header={renderSurfaceHeader("Admission Checklist Page", "Manage the move-in preparation checklist families will see on the website.", "pi pi-check-square")} footer={renderDialogFooter({ errorMessage: null, loading: false, onCancel: () => toggleModal(false), onSubmit: handleSubmit(onSubmit), submitLabel: "Save Checklist" })} style={appDialogStyle} headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle} onHide={() => toggleModal(false)}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#185da2" }}>Admission checklist page</div>
        <div className="mt-2" style={appMutedTextStyle}>Help families prepare for a smoother move-in with a checklist they can review before admission day.</div>
      </div>
      <form>
        <div style={appFormSectionStyle}>
          <div className="flex align-items-center gap-2">
            <Controller name="enabled" control={control} render={({ field }) => <Checkbox inputId="admissionChecklistEnabled" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />} />
            <label htmlFor="admissionChecklistEnabled" style={{ fontWeight: 600 }}>Add Admission Checklist page into the website</label>
          </div>
        </div>
        {[
          { name: "title", label: "Page Title", rows: 3 },
          { name: "description", label: "Description", rows: 4 },
        ].map(({ name, label, rows }) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={name}>
            <label htmlFor={name}><strong>{label}</strong></label>
            <Controller name={name as any} control={control} render={({ field }) => <InputTextarea id={name} {...field} rows={rows} style={{ width: "100%" }} />} />
          </div>
        ))}
        {categoryFields.map((category) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={category.key}>
            <label htmlFor={category.key}><strong>{category.title}</strong></label>
            <Controller name={category.key as any} control={control} render={({ field }) => <InputTextarea id={category.key} {...field} rows={7} style={{ width: "100%" }} />} />
          </div>
        ))}
      </form>
    </Dialog>
  );
};

export default EditModal;
